import { useState } from 'react';
import type { BasketItem } from '../page';

interface ParkedSale {
  id: string;
  parkedAt: string;
  parkedBy: string;
  customerName?: string;
  itemCount: number;
  total: number;
  items: BasketItem[];
}

interface ParkSaleModalProps {
  onClose: () => void;
  basketItems: BasketItem[];
  onParkSuccess: () => void;
  onRetrieveSale: (items: BasketItem[]) => void;
  initialTab?: 'park' | 'retrieve';
}

export default function ParkSaleModal({ 
  onClose, 
  basketItems, 
  onParkSuccess, 
  onRetrieveSale,
  initialTab = 'park'
}: ParkSaleModalProps) {
  const [activeTab, setActiveTab] = useState<'park' | 'retrieve'>(initialTab);
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [isParking, setIsParking] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [parkedSaleId, setParkedSaleId] = useState('');

  // Mock parked sales data
  const [parkedSales] = useState<ParkedSale[]>([
    {
      id: 'PARK-001',
      parkedAt: '2024-01-15 08:23:15',
      parkedBy: 'Sarah Johnson',
      customerName: 'John Smith',
      itemCount: 8,
      total: 67.45,
      items: []
    },
    {
      id: 'PARK-002',
      parkedAt: '2024-01-15 08:45:32',
      parkedBy: 'Mike Chen',
      customerName: 'Emily Davis',
      itemCount: 12,
      total: 134.89,
      items: []
    },
    {
      id: 'PARK-003',
      parkedAt: '2024-01-15 09:12:08',
      parkedBy: 'Sarah Johnson',
      itemCount: 5,
      total: 42.30,
      items: []
    },
    {
      id: 'PARK-004',
      parkedAt: '2024-01-15 09:34:21',
      parkedBy: 'Lisa Anderson',
      customerName: 'Robert Wilson',
      itemCount: 15,
      total: 198.76,
      items: []
    },
  ]);

  const calculateTotal = () => {
    return basketItems.reduce((sum, item) => sum + item.total, 0);
  };

  const handleParkSale = () => {
    if (basketItems.length === 0) return;

    setIsParking(true);

    setTimeout(() => {
      const newParkedId = `PARK-${String(Math.floor(Math.random() * 9000) + 1000).padStart(4, '0')}`;
      setParkedSaleId(newParkedId);
      setIsParking(false);
      setShowSuccess(true);

      setTimeout(() => {
        onParkSuccess();
        onClose();
      }, 2000);
    }, 1000);
  };

  const handleRetrieveSale = (sale: ParkedSale) => {
    // In a real implementation, this would load the actual items
    const mockItems: BasketItem[] = [
      { line: 1, code: 'MLK-001', name: 'Organic Whole Milk 1L', qty: 2, price: 4.99, discount: false, tax: true, total: 9.98 },
      { line: 2, code: 'BRD-001', name: 'Fresh Bread Whole Wheat', qty: 1, price: 3.49, discount: false, tax: true, total: 3.49 },
    ];
    
    onRetrieveSale(mockItems);
    onClose();
  };

  if (showSuccess) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-8 text-center">
          <div className="w-20 h-20 flex items-center justify-center bg-indigo-500 text-white rounded-full mx-auto mb-4">
            <i className="ri-save-line text-5xl"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2 whitespace-nowrap">Sale Parked Successfully!</h2>
          <p className="text-gray-600 mb-4 whitespace-nowrap">Transaction has been saved</p>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1 whitespace-nowrap">Reference Number</p>
            <p className="text-3xl font-bold text-indigo-600 whitespace-nowrap">{parkedSaleId}</p>
          </div>
          <p className="text-sm text-gray-500 mt-4 whitespace-nowrap">You can retrieve this sale anytime</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-indigo-600 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold whitespace-nowrap">Park & Retrieve Sales</h2>
            <p className="text-sm text-indigo-100 whitespace-nowrap">Save current transaction or retrieve a parked sale</p>
          </div>
          <button 
            onClick={onClose}
            disabled={isParking}
            className="w-8 h-8 flex items-center justify-center hover:bg-indigo-700 rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="border-b border-gray-200 bg-gray-50">
          <div className="flex">
            <button
              onClick={() => setActiveTab('park')}
              className={`flex-1 px-6 py-3 font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                activeTab === 'park'
                  ? 'bg-white text-indigo-600 border-b-2 border-indigo-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-save-line mr-2"></i>
              Park Current Sale
            </button>
            <button
              onClick={() => setActiveTab('retrieve')}
              className={`flex-1 px-6 py-3 font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                activeTab === 'retrieve'
                  ? 'bg-white text-indigo-600 border-b-2 border-indigo-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-inbox-unarchive-line mr-2"></i>
              Retrieve Parked Sale ({parkedSales.length})
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'park' ? (
            <div className="space-y-6">
              <div className="p-6 bg-indigo-50 border-2 border-indigo-200 rounded-xl">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-indigo-700 mb-1 whitespace-nowrap">Current Transaction</p>
                    <p className="text-3xl font-bold text-indigo-900 whitespace-nowrap">{basketItems.length} Items</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-indigo-700 mb-1 whitespace-nowrap">Total Amount</p>
                    <p className="text-3xl font-bold text-indigo-900 whitespace-nowrap">${calculateTotal().toFixed(2)}</p>
                  </div>
                </div>

                {basketItems.length > 0 ? (
                  <div className="bg-white rounded-lg p-4 max-h-48 overflow-y-auto">
                    <p className="text-xs font-semibold text-gray-700 mb-2 whitespace-nowrap">Items in Basket:</p>
                    {basketItems.map((item) => (
                      <div key={item.line} className="flex justify-between text-sm py-1 border-b border-gray-100 last:border-0">
                        <span className="text-gray-700">{item.qty}× {item.name}</span>
                        <span className="font-semibold text-gray-900 whitespace-nowrap">${item.total.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-lg p-6 text-center">
                    <i className="ri-shopping-basket-line text-4xl text-gray-300 mb-2"></i>
                    <p className="text-sm text-gray-500 whitespace-nowrap">No items in basket</p>
                  </div>
                )}
              </div>

              {basketItems.length > 0 && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 whitespace-nowrap">
                      Reference / Customer Name <span className="text-gray-400 font-normal">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      value={reference}
                      onChange={(e) => setReference(e.target.value)}
                      placeholder="e.g., John Smith, Table 5, Order #123"
                      disabled={isParking}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 whitespace-nowrap">
                      Notes <span className="text-gray-400 font-normal">(Optional)</span>
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add any notes about this transaction..."
                      rows={3}
                      disabled={isParking}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>

                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                    <i className="ri-information-line text-amber-600 text-xl mt-0.5"></i>
                    <div>
                      <p className="text-sm font-semibold text-amber-900 mb-1 whitespace-nowrap">About Parking Sales</p>
                      <ul className="text-xs text-amber-800 space-y-1">
                        <li>• Parked sales are saved for 24 hours</li>
                        <li>• You can retrieve them from any terminal</li>
                        <li>• Customer information is preserved</li>
                        <li>• Use this when customers need to step away</li>
                      </ul>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {parkedSales.length > 0 ? (
                parkedSales.map((sale) => (
                  <div
                    key={sale.id}
                    className="p-4 border-2 border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-all cursor-pointer"
                    onClick={() => handleRetrieveSale(sale)}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-lg font-bold text-gray-900 whitespace-nowrap">{sale.id}</p>
                        <p className="text-xs text-gray-600 whitespace-nowrap">
                          <i className="ri-time-line mr-1"></i>
                          {sale.parkedAt}
                        </p>
                      </div>
                      <button className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-semibold whitespace-nowrap transition-colors">
                        <i className="ri-inbox-unarchive-line mr-1"></i>
                        Retrieve
                      </button>
                    </div>

                    <div className="grid grid-cols-3 gap-4 mb-3">
                      <div>
                        <p className="text-xs text-gray-600 mb-1 whitespace-nowrap">Parked By</p>
                        <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">{sale.parkedBy}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1 whitespace-nowrap">Items</p>
                        <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">{sale.itemCount} items</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1 whitespace-nowrap">Total</p>
                        <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">${sale.total.toFixed(2)}</p>
                      </div>
                    </div>

                    {sale.customerName && (
                      <div className="flex items-center gap-2 text-sm text-gray-700 bg-gray-100 px-3 py-2 rounded">
                        <i className="ri-user-line"></i>
                        <span className="whitespace-nowrap">Customer: {sale.customerName}</span>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
                  <p className="text-lg font-semibold text-gray-900 mb-2 whitespace-nowrap">No Parked Sales</p>
                  <p className="text-sm text-gray-600 whitespace-nowrap">There are no parked transactions at the moment</p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between">
          <button
            onClick={onClose}
            disabled={isParking}
            className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold whitespace-nowrap transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          
          {activeTab === 'park' && (
            <button
              onClick={handleParkSale}
              disabled={isParking || basketItems.length === 0}
              className={`px-8 py-3 rounded-lg font-bold whitespace-nowrap transition-colors ${
                isParking || basketItems.length === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-700 text-white cursor-pointer'
              }`}
            >
              {isParking ? (
                <>
                  <i className="ri-loader-4-line mr-2 animate-spin"></i>
                  Parking Sale...
                </>
              ) : (
                <>
                  <i className="ri-save-line mr-2"></i>
                  Park This Sale
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
